/**
 * @lodex-pro/web-portal/hooks/useDeviceReadings — the operator admin's Device readings page
 * (Phase 13 / Task 13.13). OPERATOR_ADMIN only on the API (MANAGE_OPERATOR_TANKS); the page never
 * mounts these hooks for anyone else.
 *
 *   GET /v1/operator-portal/device-readings/gateways     the company's MQTT gateways (read-only)
 *   GET /v1/operator-portal/device-readings/issues       readings that matched no tank
 *   GET /v1/operator-portal/device-readings/connection   endpoint, port, CA and topic for the sheet
 *
 * Read-only: Paelode manages gateways (D-12). Response types are `import type` only (DEC-149 §9).
 */

import { useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import type {
  DeviceReadingIssuesResponse,
  DeviceReadingsConnectionResponse,
  DeviceReadingsGatewaysResponse,
} from '@lodex-pro/shared/validation/operator-portal-device-readings';

export const DEVICE_READINGS_KEY: readonly string[] = ['operator-portal', 'device-readings'];
const BASE = '/v1/operator-portal/device-readings';

export function useDeviceGateways(): ReturnType<
  typeof useApiQuery<DeviceReadingsGatewaysResponse>
> {
  const opts = useApiOpts();
  return useApiQuery<DeviceReadingsGatewaysResponse>(opts, `${BASE}/gateways`, {
    queryKey: [...DEVICE_READINGS_KEY, 'gateways'],
    staleTime: 30_000,
  });
}

export function useDeviceReadingIssues(): ReturnType<
  typeof useApiQuery<DeviceReadingIssuesResponse>
> {
  const opts = useApiOpts();
  return useApiQuery<DeviceReadingIssuesResponse>(opts, `${BASE}/issues?limit=100`, {
    queryKey: [...DEVICE_READINGS_KEY, 'issues'],
    staleTime: 30_000,
  });
}

export function useDeviceConnection(): ReturnType<
  typeof useApiQuery<DeviceReadingsConnectionResponse>
> {
  const opts = useApiOpts();
  return useApiQuery<DeviceReadingsConnectionResponse>(opts, `${BASE}/connection`, {
    queryKey: [...DEVICE_READINGS_KEY, 'connection'],
    staleTime: 5 * 60_000,
  });
}
